import { UserPlanVo, UserProfile } from "@/api";
import inviteGiftIcon from "@/assets/images/invite-gift-icon.png";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import classNames from "classnames";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React from "react";
import Button from "../Button";
import { AuthActions } from "./components/AuthActions";
import { CreditPopover } from "./components/CreditPopover";
import { Logo } from "./components/Logo";
import { SocialButtons } from "./components/SocialButtons";
import { UserPopover } from "./components/UserPopover";
import styles from "./index.module.css";

export interface HeaderProps {
  userLoading: boolean;
  user: UserProfile | null;
  userPlan: UserPlanVo | null;
  isGifted?: boolean;
  userCredits: number;
  onCreditUsageClick: () => void;
  onInviteClick: () => void;
}

const feedbackFormBaseUrl =
  "https://j0yswlgboxz.feishu.cn/share/base/form/shrcnHs6ytPFjKYMWQzj9QrFOxd?prefill_Project+Link=";

export const Header: React.FC<HeaderProps> = ({
  userLoading,
  user,
  userPlan,
  isGifted,
  userCredits,
  onCreditUsageClick,
  onInviteClick,
}) => {
  const router = useRouter();
  const [feedbackUrl, setFeedbackUrl] = React.useState(feedbackFormBaseUrl);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set("_t", Date.now().toString());
    if (user?.id) {
      url.searchParams.set("_uid", user.id.toString());
    }
    const encodedUrl = encodeURIComponent(url.toString());
    setFeedbackUrl(`${feedbackFormBaseUrl}${encodedUrl}`);
  }, [user]);

  // 预热 Pricing 拦截路由的 UI 资源，减少首跳等待
  React.useEffect(() => {
    router.prefetch("/pricing");
    router.prefetch("/feedback");
    void import("@/component/FullScreenModal");
  }, [router]);

  return (
    <header
      className={classNames(
        "w-full transition-all duration-300",
        "h-[56px] sm:h-[84px] px-4 sm:px-5 md:px-8 lg:px-10 xl:px-11",
        {
          [styles.blur]: true,
        },
      )}
    >
      <div className="h-full flex items-center justify-between">
        {/* Logo 区域 */}
        <Logo />

        {/* 右侧功能区 */}
        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            type="plain"
            className="flex items-center justify-center border border-solid border-text-primary/[.07] rounded-full bg-black/20 backdrop-blur-xl transition-all hover:bg-primary/[.15] hover:backdrop-blur-none w-10 h-10 md:w-auto md:px-6 cursor-pointer"
            aria-label="Earn Credits"
            onClick={() => {
              logger.event(VidMuseEvent.InviteButtonClick, {
                is_logged_in: !!user,
              });
              if (!user) {
                router.push("/login");
                return;
              }
              onInviteClick();
            }}
          >
            <Image
              src={inviteGiftIcon.src}
              alt="Invite Gift"
              width={20}
              height={20}
            />

            <span
              className={classNames(styles.earnCreditsText, "hidden md:block")}
            >
              Earn Credits
            </span>
          </Button>

          {/* 社交按钮 */}
          <SocialButtons />

          {user ? (
            <>
              <CreditPopover
                user={user}
                userPlan={userPlan}
                isGifted={isGifted}
                userLoading={userLoading}
                userCredits={userCredits}
                onCreditUsageClick={onCreditUsageClick}
              />
              <UserPopover
                user={user}
                userPlan={userPlan}
                isGifted={isGifted}
                feedbackUrl={feedbackUrl}
              />
            </>
          ) : (
            <AuthActions
              userLoading={userLoading}
              user={user}
              userPlan={userPlan}
              userCredits={userCredits}
            />
          )}
        </div>
      </div>
    </header>
  );
};
